import React from "react";
import { menuHoverMarker } from "@/utils/header-navigation";
import Link from "next/link";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/sharp-light-svg-icons";

interface MenuItemProps {
  href: string;
  onClick: () => void;
  children: React.ReactNode;
  customIconName?: string;
  redirectToExternal?: boolean;
  openInNewTab?: boolean;
  externalUrl?: string;
}

const MenuIcon: React.FC<Pick<MenuItemProps, "customIconName">> = ({
  customIconName,
}) => {
  const className =
    "size-5 opacity-0 text-[var(--menu-text-color)] group-hover:opacity-100 transition-opacity duration-300";

  if (menuHoverMarker(customIconName) === "slashes") {
    return (
      <span
        className={`${className} inline-flex items-center justify-center text-lg font-semibold leading-none`}
        style={{ color: "#bf8f24" }}
      >
        {"//"}
      </span>
    );
  }

  return <FontAwesomeIcon className={className} icon={faChevronRight} />;
};

const MenuItem: React.FC<MenuItemProps> = ({
  href,
  onClick,
  children,
  customIconName,
  redirectToExternal,
  openInNewTab,
  externalUrl,
}) => {
  const content = (
    <div className="flex items-center gap-0.3 group">
      <MenuIcon customIconName={customIconName} />
      <span className="group-hover:translate-x-2 transition-transform duration-300">
        {children}
      </span>
    </div>
  );

  // If redirectToExternal is true, use external URL and potentially open in new tab
  if (redirectToExternal && externalUrl) {
    return (
      <a
        href={externalUrl}
        target={openInNewTab ? "_blank" : "_self"}
        rel={openInNewTab ? "noopener noreferrer" : ""}
        onClick={onClick}
        className="data-gtm-menu-item"
        aria-label="Menu"
      >
        {content}
      </a>
    );
  }

  // Default behavior for internal pages
  return (
    <Link
      aria-label="Menu"
      href={href}
      onClick={onClick}
      prefetch={true}
      className="data-gtm-menu-item"
    >
      {content}
    </Link>
  );
};

export default MenuItem;
